/**
 * Bulk-send patient filter → indexing-service `FilterRequest` (PTV2-1162).
 *
 * The Compose step collects a small set of patient criteria; this compiles them into the
 * `FilterRequest` body that `/api/v1/patients/filter-search` (and, nested under `query`, the
 * bulk-send preview/launch endpoints) understands. Field names are the camelCase `SearchableField`
 * wire names supported by the patient search registry: `firstName`, `lastName`, `dateOfBirth`
 * (range), `sex`, `lifeStatus`. An empty filter compiles to `filter: null` — match-all (every
 * patient in the tenant).
 */

/** Page size the resolver uses when paging the cohort; 200 is the search cap. */
const RESOLVE_PAGE_SIZE = 200;

export const SEX_VALUES = ['F', 'M', 'U', 'O', 'UNASSIGNED', 'INTERSEX'] as const;
export type SexValue = (typeof SEX_VALUES)[number];

export const LIFE_STATUS_VALUES = [
  'ALIVE',
  'DECEASED',
  'UNBORN',
  'STILLBORN',
  'MISCARRIAGE',
  'ABORTED',
] as const;
export type LifeStatusValue = (typeof LIFE_STATUS_VALUES)[number];

/** The Compose-step patient filter (all optional; blank = unconstrained). */
export interface BulkSendPatientFilter {
  readonly firstName: string;
  readonly lastName: string;
  /** ISO date (YYYY-MM-DD); lower bound of the DOB range. */
  readonly dobFrom: string;
  /** ISO date (YYYY-MM-DD); upper bound of the DOB range. */
  readonly dobTo: string;
  readonly sex: '' | SexValue;
  readonly lifeStatus: '' | LifeStatusValue;
}

export const EMPTY_FILTER: BulkSendPatientFilter = {
  firstName: '',
  lastName: '',
  dobFrom: '',
  dobTo: '',
  sex: '',
  lifeStatus: '',
};

interface MatchClause {
  readonly type: 'match';
  readonly field: string;
  readonly value: string;
}

interface RangeClause {
  readonly type: 'range';
  readonly field: string;
  readonly from?: string;
  readonly to?: string;
}

interface BooleanClause {
  readonly type: 'boolean';
  readonly operation: 'and';
  readonly filters: readonly FilterClause[];
}

export type FilterClause = MatchClause | RangeClause | BooleanClause;

/** The `FilterRequest` body sent as the bulk-send `query`. `filter: null` means match-all. */
export interface FilterRequestBody {
  readonly filter: FilterClause | null;
  readonly limit: number;
}

function trimmed(value: string): string {
  return value.trim();
}

/** Compile the Compose-step filter into a `FilterRequest` body. */
export function buildFilterRequest(filter: BulkSendPatientFilter): FilterRequestBody {
  const clauses: FilterClause[] = [];

  if (trimmed(filter.lastName).length > 0) {
    clauses.push({ type: 'match', field: 'lastName', value: trimmed(filter.lastName) });
  }
  if (trimmed(filter.firstName).length > 0) {
    clauses.push({ type: 'match', field: 'firstName', value: trimmed(filter.firstName) });
  }

  const from = trimmed(filter.dobFrom);
  const to = trimmed(filter.dobTo);
  if (from.length > 0 || to.length > 0) {
    clauses.push({
      type: 'range',
      field: 'dateOfBirth',
      ...(from.length > 0 ? { from } : {}),
      ...(to.length > 0 ? { to } : {}),
    });
  }

  if (filter.sex.length > 0) {
    clauses.push({ type: 'match', field: 'sex', value: filter.sex });
  }
  if (filter.lifeStatus.length > 0) {
    clauses.push({ type: 'match', field: 'lifeStatus', value: filter.lifeStatus });
  }

  return { filter: combine(clauses), limit: RESOLVE_PAGE_SIZE };
}

/** No clauses → match-all (null); one → that clause; many → AND. */
function combine(clauses: readonly FilterClause[]): FilterClause | null {
  if (clauses.length === 0) {
    return null;
  }
  if (clauses.length === 1) {
    return clauses[0] ?? null;
  }
  return { type: 'boolean', operation: 'and', filters: clauses };
}

/** True when the filter has no criteria — i.e. it targets every patient (a deliberate, risky choice). */
export function isMatchAll(filter: BulkSendPatientFilter): boolean {
  return buildFilterRequest(filter).filter === null;
}
